<template>
  <el-row>
    <el-col>
      <Filters
        class="mb-3"
        :resource="$sdk.account_contact"
        :filters.sync="filters"
        @apply="reload"
        @ready="load"
      />

      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <AccountContactsTable :account-contacts="contacts">
          <template
            v-if="canUpdate || canDelete"
            v-slot:actions="{ accountContact, accountContactIndex }"
          >
            <el-button
              v-if="canUpdate"
              size="mini"
              @click.native="edit(accountContact)"
            >
              Edit
            </el-button>

            <el-button
              v-if="canDelete"
              size="mini"
              type="danger"
              @click.native="remove(accountContact, accountContactIndex)"
            >
              Delete
            </el-button>
          </template>
        </AccountContactsTable>

        <el-pagination
          class="text-center pt-3"
          :current-page.sync="contacts.page"
          :page-size.sync="contacts.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="contacts.total"
          @size-change="reload"
          @current-change="reload"
        />
      </el-card>
    </el-col>
  </el-row>
</template>

<script>
import Filters from '@/components/Filters/Complex'
import AccountContactsTable from '@/components/Application/CustomerService/Accounts/Account/Contacts/Table'
import AccountContactForm from '@/components/Application/CustomerService/Accounts/Account/Contacts/Contact/Form'
import { CREATE, UPDATE, DELETE } from '@/plugins/user'

export default {
  components: {
    Filters,
    AccountContactsTable
  },
  data () {
    return {
      breadcrumb: {
        name: 'Contacts',
        route: { name: 'customerservice-accounts-account-contacts', params: { accountId: this.$route.params.accountId } }
      },
      actions: [
        {
          hidden: this.$user.cannot(CREATE, 'account_contact'),
          name: 'Create',
          icon: ['fal', 'plus'],
          event: this.create
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.reload
        }
      ],
      isLoading: true,
      contacts: [],
      filters: []
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, 'account_contact')
    },
    canDelete () {
      return this.$user.can(DELETE, 'account_contact')
    }
  },
  methods: {
    load () {
      this
        .$sdk
        .account_contact
        .list(request => {
          request.context = { resource: 'account', identifier: this.$route.params.accountId }
          request.filters = this.filters
          request.include = [ 'contact_type' ]
          request.order = 'name:asc'
        })
        .then(contacts => {
          this.contacts = contacts
        })
        .finally(() => { this.isLoading = false })
    },
    create () {
      const contact = new this.$sdk.account_contact()
      const form = this.$createElement(AccountContactForm, { props: { accountContact: contact }, ref: 'create-account-contact-form' })

      this
        .$msgbox({
          title: 'New Account Contact',
          message: form,
          showCancelButton: true,
          confirmButtonText: 'Create',
          cancelButtonText: 'Cancel',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              this
                .$refs['create-account-contact-form']
                .validate(valid => {
                  if (valid) {
                    instance.confirmButtonLoading = true

                    contact
                      .create(request => {
                        request.context = { resource: 'account', identifier: this.$route.params.accountId }
                      })
                      .then(done)
                      .finally(() => { instance.confirmButtonLoading = false })
                  }
                })
            } else {
              done()
            }
          }
        })
        .then(() => {
          this.$message.success('Account Contact successfully created')
          this.reload()
        })
    },
    edit (contact) {
      const form = this.$createElement(AccountContactForm, { props: { accountContact: contact }, ref: 'edit-account-contact-form' })

      this
        .$msgbox({
          title: 'Edit Account Contact',
          message: form,
          showCancelButton: true,
          confirmButtonText: 'Update',
          cancelButtonText: 'Cancel',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              this
                .$refs['edit-account-contact-form']
                .validate(valid => {
                  if (valid) {
                    instance.confirmButtonLoading = true

                    contact
                      .create(request => {
                        request.context = { resource: 'account', identifier: this.$route.params.accountId }
                      })
                      .then(done)
                      .finally(() => { instance.confirmButtonLoading = false })
                  }
                })
            } else {
              done()
            }
          }
        })
        .then(() => {
          this.$message.success('Account Contact successfully updated')
          this.reload()
        })
        .catch(err => {
          contact.reset()
          throw err
        })
    },
    remove (contact) {
      this
        .$confirm('This will permanently delete the Account Contact. Continue?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              instance.confirmButtonLoading = true

              contact
                .delete(request => {
                  request.context = { resource: 'account', identifier: this.$route.params.accountId }
                })
                .then(done)
                .finally(() => { instance.confirmButtonLoading = false })
            } else {
              done()
            }
          }
        })
        .then(() => {
          this.$message.success('Account Contact successfully deleted')
          this.reload()
        })
    },
    async reload () {
      this.isLoading = true

      try {
        await this.contacts.reload(request => { request.filters = this.filters })
      } catch {
        this.$message.error('Failed to load Account Contacts')
      }

      this.isLoading = false
    }
  }
}
</script>
